/**
 * Angular import
 */
import { Component, Input, OnDestroy } from '@angular/core';

/**
 * constant imports
 */
import { COMMUNITY_JOIN_TYPE, COMMUNITY_PRIVACY, COMMUNITY_REGEX, ERROR_CODES, NUMBERS } from '../../../constants/app.constants';

/**
 * service imports
 */
import { NewLanguageService } from '../../../dataservices/new-language/new-language.service';
import { FunCollectionService } from '../../common-functions/fun-collection.service';

/**
 * model imports
 */
import { CommunityConfirmation, ConfirmationModalData, GrCommunity, PostedIn } from '../../../onboarding/interface/global.model';


/**
 * RxJs imports
 */
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
/**
 * Componrtn to display community and association details
 */
@Component({
  selector: 'app-posted-in',
  templateUrl: './posted-in.component.html',
  styleUrls: ['./posted-in.component.scss']
})
export class PostedInComponent implements OnDestroy {

@Input() product;  
/**
 * posted click event
 */
 @Input() isPostedEvent?:boolean;

/**
 * number constant
 */
public numbers = NUMBERS;
/**
 * flag to show posted in details
 */
public showPostedIn = false;
/**
 * @notifier$ defines a notifier subject stream to end data streaming.
 */
private notifier$ = new Subject();
/**
 * @confirmedCommunity confirmation message and status
 * @confirmationModal modal status
 * @confirmationText confirmation message
 * @withdrawRequest withdraw request object
 */
public confirmedCommunity: CommunityConfirmation;
public confirmationModal = false;
public confirmationText: ConfirmationModalData;
public withdrawRequest: CommunityConfirmation;
public userId: string;

  constructor(
    private fcs: FunCollectionService,
    public mls: NewLanguageService,
  ) { }

  /**
   * total count for feed posted in community/association
   */
  get totalPostedCount(): number {
    const count = this.product.mapping_count;
    return (count.association + count.community) || NUMBERS.ZERO;
  }

  /**
   * total count for feed posted in community
   */
  get postedCommunitySize(): number {
    return this.product.mapping_count.community || NUMBERS.ZERO;
  }

  /**
   * remaining count for feed posted in community user not part of
   */
  get remainingCommunity(): number {
    return (this.product.mapping_count.community - this.product.community.length);
  }

  /**
   * remaining count for feed posted in community user not part of text
   */
  get remainingCommunityText(): string {
    const regex = COMMUNITY_REGEX.COUNT;
    let msg = '';
    if (this.product.community.length) {
      msg = this.mls.languageText.community.key146;
    }
    msg += ` actual_count ${this.mls.languageText.community.key145} `;
    if (this.remainingCommunity > NUMBERS.ONE) {
      msg += this.mls.languageText.community.key148;
    } else {
      msg += this.mls.languageText.community.key147;
    }
    return this.fcs.replaceWithActualValue(regex, msg, this.remainingCommunity.toString());
  }

  /**
   * remaining count for feed posted in association text
   */
  get remainingAssociationsText(): string {
    const regex = COMMUNITY_REGEX.COUNT;
    let msg = '';
    if (this.product.user_feed_association_list.length) {
      msg = this.mls.languageText.community.key146;
    }
    msg += ` actual_count ${this.mls.languageText.community.key145} `;
    if (this.remainingAssociation > NUMBERS.ONE) {
      msg += this.mls.languageText.community.key149;
    } else {
      msg += this.mls.languageText.community.key144;
    }
    return this.fcs.replaceWithActualValue(regex, msg, this.remainingAssociation.toString());
  }

  /**
   * total count for feed posted in association
   */
  get postedAssociationSize(): number {
    return this.product.mapping_count.association || NUMBERS.ZERO;
  }
  /**
   * remaining count for feed posted in association
   */
  get remainingAssociation(): number {
    return (this.product.mapping_count.association - this.product.user_feed_association_list.length);
  }

  /**
   * returns privacy type of community
   */
  privacyType(community: GrCommunity): string {
    if (!community?.privacy) {
      return;
    }
    if (community?.privacy === COMMUNITY_PRIVACY.PRIVATE) {
      return this.mls.languageText.community.key67;
    }
    if (community?.privacy === COMMUNITY_PRIVACY.PUBLIC) {
      return this.mls.languageText.community.key66;
    }
  }

  /**
   * @param index returns card CTA text
   * @returns  type
   */
  getCtaText(community: GrCommunity): string {
    return (community?.join_type === COMMUNITY_JOIN_TYPE.APPROVAL
      ? (community.withdrawRequest || +community?.request?.response === NUMBERS.TWO ? this.mls.languageText.community.key88 : this.mls.languageText.community.key103)
      : this.mls.languageText.community.key72);
  }

  /**
   * returns checks to show join community CTA
   */
  showJoinButton(community: GrCommunity): boolean {
    return community?.privacy === COMMUNITY_PRIVACY.PUBLIC &&
      community?.flags?.show_join_button === NUMBERS.ONE;
  }

  /**
   * Join public community event handler
   * @param community gr
   */
  joinCommunity(community: GrCommunity): void {
    if (community?.withdrawRequest || +community?.request?.response === NUMBERS.TWO) {
      this.withdrawRequest = {
        openModal: community?.withdrawRequest || +community?.request?.response === NUMBERS.TWO,
        community: community,
        callingFrom: ''
      };
      this.withdrawConfirmation(this.withdrawRequest);
    } else {
      community?.join_type === COMMUNITY_JOIN_TYPE.APPROVAL
        ? this.requestToJoinHandler(community)
        : this.joinCommunityHandler(community);
    }
  }

  /**
   * Event handler for sending request to admin to join public community
   */
  requestToJoinHandler(community: GrCommunity): void {
    community.loading = true;
    this.fcs.joinPublicCommunity(community.community_id, NUMBERS.ONE.toString()).pipe(
      takeUntil(this.notifier$)
    ).subscribe(response => {
      community.loading = false;
      if (response.status === NUMBERS.ONE && response.code === NUMBERS.TWO_THOUSAND) {
        this.fcs.showSuccess(this.mls.languageText.community.key89, this.mls.languageText.toster.key2);
        community.withdrawRequest = true;
      }
      if (response.error && response.error.code === ERROR_CODES.ALREADY_EXIST) {
        community.withdrawRequest = false;
        this.fcs.showSuccess(response.error.msg, this.mls.languageText.toster.key2);
      }
    });
  }

  /**
  * Join public community eventhandler
  */
  joinCommunityHandler(community: GrCommunity): void {
    community.loading = true;
    this.fcs.joinPublicCommunity(community.community_id).pipe(
      takeUntil(this.notifier$)
    ).subscribe(response => {
      community.loading = false;
      if (response.status === NUMBERS.ONE) {
        this.fcs.showSuccess(this.mls.languageText.toster.key2, this.mls.languageText.toster.key2);
        this.fcs.navigateToCommunityDetailAndReload(community.community_id);
      } else {
        this.fcs.errorHandeler(response);
      }
    },
      (error) => {
        community.loading = false;
        this.fcs.errorHandeler(error);
      });
  }

  /**
   * Handler to navigate to community details
   * @param community_id  for navigate
   */
  navigateToDetail(community_id: string): void {
    this.fcs.navigateToCommunityDetailAndReload(community_id);
  }

  /**
   * getter for postedIn details
   */
  get postedIn(): PostedIn {
    if (!this.product) {
      return null;
    }
    return this.fcs.getPostedInDetail(this.product);
  }

  /**
   * event handler for open feed detail
   * @param event  for feed
   */
  handleOpenFeedEvent(event: boolean): void {
    this.showPostedIn = event;
    this.isPostedEvent = false;
  }

  /**
   * event handler for community detail navigation
   * @param community_id  for navigate
   */
  handleCommunityDetailEvent(community_id: string): void {
    this.fcs.navigateToCommunityDetailAndReload(community_id);
  }

  /**
   * Confirmation withdraw request.
   */
  withdrawConfirmation(communityConfirmation: CommunityConfirmation): void {
    if (!this.confirmationText) {
      this.setConfirmationText();
      this.requestWithdrawStatus();
    }
    this.confirmedCommunity = communityConfirmation;
    this.confirmationModal = communityConfirmation.openModal;
  }

  /**
   * sets confirmation message for withdraw request
   */
  setConfirmationText(): void {
    this.confirmationText = {
      bodyText: this.mls.languageText.community.key91,
      headingText: this.mls.languageText.community.key92,
      allowButtonText: this.mls.languageText.community.key93,
      cancelButtonText: this.mls.languageText.community.key94
    };
  }

  /**
  * Reset confirm modal.
  */
  resetConfirmModal(): void {
    this.confirmationModal = false;
  }
  /**
   * Allow withdraw request.
   */
  allowAction(isAllow: boolean): void {
    if (isAllow) {
      this.resetConfirmModal();
    }
  }
  /**
   * Withdraw actions to get on card CTA click.
   */
  requestWithdrawStatus(): void {
    this.fcs.getWithdrawStatus.
      pipe(
        takeUntil(this.notifier$)
      )
      .subscribe(() => {
        this.withdrawRequest.community.loading = true;
        if (this.withdrawRequest.community) {
          this.fcs.joinPublicCommunity(
            this.withdrawRequest.community.community_id,
            this.numbers.ZERO.toString()).pipe(
              takeUntil(this.notifier$)
            ).subscribe(response => {
              this.withdrawRequest.community.loading = false;
              if (response.status === this.numbers.ONE && response.code === this.numbers.TWO_THOUSAND) {
                this.withdrawRequest.community.request.response = this.numbers.ZERO;
                this.withdrawRequest.community.request.type = '';
                this.fcs.showSuccess(this.mls.languageText.community.key90, this.mls.languageText.toster.key2);
                this.withdrawRequest.community.withdrawRequest = false;
              }
              if (response.error && response.error.code === ERROR_CODES.ALREADY_EXIST) {
                this.fcs.showSuccess(response.error.msg, this.mls.languageText.toster.key2);
              }
            },
              (error) => {
                this.withdrawRequest.community.loading = false;
                this.fcs.errorHandeler(error);
              });
        }
      });
  }
  ngOnDestroy(): void {
    this.notifier$.next();
    this.notifier$.complete();
    this.showPostedIn = false;
  }
}
